import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { AsyncTypeahead, TypeaheadRef } from 'react-bootstrap-typeahead';
import { Option } from 'react-bootstrap-typeahead/types/types';
import { getSearchedLocations } from 'src/redux/services/practiceServices';
import {
  Address,
  AddressObj,
  LocationSearchResponse,
  PageQuestionChoice,
} from '../../types';
import './TypeAheadInput.scss';
import { NON_CHARACTER_KEYS , VALID_ZIP_TYPE, ZIP_TYPE_KEY} from './TypeAheadInputConstant';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const MENU_AUTO_HIDE_DELAY_MS = 1000;

type SearchLifecycleState = {
  isValid: boolean;
  isLoading: boolean;
  rawValue: string;
};

type Props = {
  choices: any[];
  onLocationSelection: (
    selectedOption: PageQuestionChoice,
    choiceId: string,
  ) => void;
  type: 'questionnaire' | 'CAfilters';
  disabled?: boolean;
  title?: string;
  infoMessage?: any;
  displayField?: keyof Address;
  restrictInputToDigits?: boolean;
  searchTriggerLength?: number;
  emptyResultsMessage?: string;
  selectionRequiredMessage?: string;
  searchingStatusText?: string;
  blockedLocationMessage?: string;
  blockInvalidOfficeLocations?: boolean;
  isValid?: boolean;
  onSearchLifecycleChange?: (state: SearchLifecycleState) => void;
};

export function TypeAheadLocationSearch({
  choices,
  onLocationSelection,
  type,
  disabled,
  title,
  infoMessage,
  displayField,
  restrictInputToDigits,
  searchTriggerLength = 5,
  emptyResultsMessage,
  selectionRequiredMessage,
  searchingStatusText,
  blockedLocationMessage,
  blockInvalidOfficeLocations = false,
  isValid,
  onSearchLifecycleChange,
}: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<Address[]>([]);
  const [selected, setSelected] = useState<Option[]>([]);
  const [selectedValue, setSelectedValue] = useState<Option[]>([]);
  const [hasEmptyResults, setHasEmptyResults] = useState(false);
  const [showSelectionRequired, setShowSelectionRequired] = useState(false);
  const [hasBlockedLocation, setHasBlockedLocation] = useState(false);
  const choice = choices[0];

  useEffect(() => {
    if (choices[0]) {
      const _choice = choices[0];

      if (displayField) {
        if (_choice?.value) {
          setSelected([_choice.value]);
          setSelectedValue([_choice.value]);
        }

        return;
      }

      const _choiceValue: Address = _choice?.value
        ? JSON.parse(_choice?.value)
        : undefined;

      if (_choiceValue) {
        setSelected([_choiceValue]);
        setSelectedValue([_choiceValue]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // `AsyncTypeahead` (react-bootstrap-typeahead's `useAsync`) tears down and
  // recreates its internal debounce timer whenever the `onSearch` prop's
  // identity changes. When `onSearchLifecycleChange` is wired up (the zip
  // use case), this component re-renders on every keystroke (redux
  // dispatches bubble back down as new props), so `handleSearch` must stay
  // referentially stable across renders - otherwise each keystroke's own
  // re-render cancels that same keystroke's just-scheduled debounced
  // search before it ever fires, and the API is never called. Route the
  // values it needs through refs kept in sync via an effect, instead of
  // closing over the props directly.
  const restrictInputToDigitsRef = useRef(restrictInputToDigits);
  const searchTriggerLengthRef = useRef(searchTriggerLength);
  const onSearchLifecycleChangeRef = useRef(onSearchLifecycleChange);
  const displayFieldRef = useRef(displayField);
  const onSelectionChangeRef = useRef<(newSelection: Option[]) => void>();
  // Latest (sanitized) text in the input, so a response for a query the
  // user has since edited can't auto-select a value they no longer see.
  const latestInputRef = useRef('');
  const typeaheadRef = useRef<TypeaheadRef>(null);
  const hideMenuTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(hideMenuTimerRef.current), []);

  useEffect(() => {
    restrictInputToDigitsRef.current = restrictInputToDigits;
    searchTriggerLengthRef.current = searchTriggerLength;
    onSearchLifecycleChangeRef.current = onSearchLifecycleChange;
    displayFieldRef.current = displayField;
  });

  const sanitize = (query: string) =>
    restrictInputToDigits
      ? query.replace(/\D/g, '').slice(0, searchTriggerLength)
      : query;

  const sanitizeStable = useCallback((query: string) => {
    return restrictInputToDigitsRef.current
      ? query.replace(/\D/g, '').slice(0, searchTriggerLengthRef.current)
      : query;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Zip use case: once a search has settled (zip auto-selected, or no
  // match found), leave the dropdown up briefly so the user sees the
  // outcome, then close it. Skipped for a response the user has already
  // typed past, so it can't close the dropdown of a newer search.
  const scheduleMenuHide = useCallback((keyword: string) => {
    if (!displayFieldRef.current || latestInputRef.current !== keyword) {
      return;
    }

    clearTimeout(hideMenuTimerRef.current);
    hideMenuTimerRef.current = setTimeout(() => {
      typeaheadRef.current?.hideMenu();
    }, MENU_AUTO_HIDE_DELAY_MS);
  }, []);

  const fetchSearchedLocations = useCallback(async (keyword: string) => {
    try {
      const locations: LocationSearchResponse = await getSearchedLocations(
        keyword,
      );

      if (locations?.locationDtoList?.length) {
        const formattedLocations = formatAddressForDisplay(
          locations.locationDtoList,
        );

        setOptions(formattedLocations);
        setHasEmptyResults(false);

        // Zip use case: when the typed zip is itself one of the
        // suggestions, select it for the user instead of making
        // them pick it from the list.
        const field = displayFieldRef.current;
        const typedMatch =
          field && latestInputRef.current === keyword
            ? formattedLocations.find(
                location =>
                  String(location[field as keyof typeof location]).padStart(
                    searchTriggerLengthRef.current,
                    '0',
                  ) === keyword,
              )
            : undefined;

        if (typedMatch) {
          // If the typed match is natively disabled by our logic, block auto-selection and show error
          if ((typedMatch as any).disabled && blockInvalidOfficeLocations) {
            setHasBlockedLocation(true);
            onSearchLifecycleChangeRef.current?.({
              isValid: false,
              isLoading: false,
              rawValue: keyword,
            });
            scheduleMenuHide(keyword);
          } else {
            onSelectionChangeRef.current?.([typedMatch]);
            scheduleMenuHide(keyword);
          }
        }
      } else {
        setOptions([]);
        setHasEmptyResults(true);
        onSearchLifecycleChangeRef.current?.({
          isValid: false,
          isLoading: false,
          rawValue: keyword,
        });
        scheduleMenuHide(keyword);
      }
    } catch {
      setOptions([]);
      setHasEmptyResults(true);
      onSearchLifecycleChangeRef.current?.({
        isValid: false,
        isLoading: false,
        rawValue: keyword,
      });
      scheduleMenuHide(keyword);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = useCallback(
    (query: string) => {
      const sanitizedQuery = sanitizeStable(query);

      setIsLoading(true);
      setOptions([]);
      setHasEmptyResults(false);
      onSearchLifecycleChangeRef.current?.({
        isValid: false,
        isLoading: true,
        rawValue: sanitizedQuery,
      });
      fetchSearchedLocations(sanitizedQuery);
    },
    [fetchSearchedLocations, sanitizeStable],
  );

  function formatAddressForDisplay(data: Address[]): AddressObj[] {
    const reformattedArray: AddressObj[] = data.map((address: any) => {
      const hasZipType = ZIP_TYPE_KEY in address;
      const isBlocked = blockInvalidOfficeLocations && hasZipType ? (address.zipType !== VALID_ZIP_TYPE || address.allowedForOffice !== true) : false;

      return Object.assign(address, {
        displayLabel: `${address.city}, ${address.stateCode} ${address.zipCode}, ${address.countryCode}`,
        disabled: isBlocked,
      });
    });

    return reformattedArray;
  }

  // Bypass client-side filtering by returning `true`. Results are already
  // filtered by the search endpoint, so no need to do it again.
  const filterBy = () => true;

  const onSelectionChange = (newSelection: Option[]) => {
    if (!newSelection?.length) {
      setSelected(newSelection);

      return;
    }

    const selectedOption = newSelection[0] as PageQuestionChoice;

    if (displayField) {
      // zipCode is typed as a number - pad back any leading zero
      // (e.g. Boston 02134) lost in that conversion.
      const displayValue = String(
        (selectedOption as any)[displayField],
      ).padStart(searchTriggerLength, '0');

      setSelected([displayValue]);
      setSelectedValue([displayValue]);
      onSearchLifecycleChange?.({
        isValid: true,
        isLoading: false,
        rawValue: displayValue,
      });
    } else {
      setSelected(newSelection);
      setSelectedValue(newSelection);
    }

    setHasEmptyResults(false);
    setShowSelectionRequired(false);
    setHasBlockedLocation(false);
    onLocationSelection(selectedOption, choice.id);
  };

  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  });

  const onInputChange = (text: string) => {
    const sanitizedText = sanitize(text);

    latestInputRef.current = sanitizedText;
    clearTimeout(hideMenuTimerRef.current);
    setHasEmptyResults(false);
    setShowSelectionRequired(false);
    setHasBlockedLocation(false);

    if (sanitizedText.length < searchTriggerLength) {
      setOptions([]);
    }

    onSearchLifecycleChange?.({
      isValid: false,
      isLoading: false,
      rawValue: sanitizedText,
    });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!restrictInputToDigits || e.metaKey || e.ctrlKey) {
      return;
    }

    if (!/^\d$/.test(e.key) && !NON_CHARACTER_KEYS.includes(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <div className="typeahead-location-search-container">
      <div className={type === 'questionnaire' ? '' : 'w-100'}>
        {title && (
          <label className="typeahead-location-search-title">{title}</label>
        )}
        <div className="typeahead-input-wrapper">
        <AsyncTypeahead
          ref={typeaheadRef}
          id={choice?.unique_key || 'typeahead-location-search'}
          className={`typeahead-location-search ${
            type === 'questionnaire'
              ? 'questionnaire-location-search'
              : 'filter-location-search'
          }`}
          filterBy={filterBy}
          onChange={onSelectionChange}
          onInputChange={onInputChange}
          onKeyDown={onKeyDown}
          isLoading={isLoading}
          labelKey="displayLabel"
          selected={selected}
          minLength={searchTriggerLength}
          inputProps={
            restrictInputToDigits
              ? { maxLength: searchTriggerLength }
              : undefined
          }
          onSearch={handleSearch}
          // Zip use case: a cached query skips `onSearch`, so
          // retyping a zip that was already looked up would
          // neither be auto-selected nor show
          // `emptyResultsMessage` again.
          useCache={!displayField}
          options={options}
          placeholder={choice?.place_holder}
          onBlur={() => {
            // Only the original (non-zip) usage reverts to the
            // last confirmed selection on blur. For the zip case
            // (`displayField`), silently snapping back to a
            // stale valid value while an error is still showing
            // and Continue is disabled would misleadingly imply
            // the field is still valid - leave whatever the
            // user actually typed/selected in place instead.
            if (!displayField) {
              setSelected(selectedValue);

              return;
            }

            // Leaving the zip field without picking a
            // suggestion closes the list and keeps Continue
            // disabled - tell the user a selection is needed.
            setShowSelectionRequired(!selected.length);
          }}
          renderMenuItemChildren={(option: any) => (
            <span>{`${option.city}, ${option.stateCode} ${option.zipCode}, ${option.countryCode}`}</span>
          )}
          disabled={disabled}
        />
        {isValid && selected.length > 0 && !hasBlockedLocation && !isLoading && (
            <FontAwesomeIcon className="check-icon" icon={faCheckCircle} />
        )}
        </div>

        {infoMessage && (
          <label className="typeahead-location-search-info">
            {infoMessage}
          </label>
        )}

        {/* MUTUALLY EXCLUSIVE ERRORS: Only show one at a time */}
        {!isLoading && hasBlockedLocation && blockedLocationMessage && (
          <label className="invalid-feedback blocked-zip-error">
            {blockedLocationMessage}
          </label>
        )}
        {!isLoading && !hasBlockedLocation && hasEmptyResults && emptyResultsMessage && (
          <label className="invalid-feedback">{emptyResultsMessage}</label>
        )}
        {!isLoading &&
          !hasBlockedLocation &&
          !hasEmptyResults &&
          showSelectionRequired &&
          selectionRequiredMessage && (
            <label className="invalid-feedback">
              {selectionRequiredMessage}
            </label>
          )}
        {isLoading && searchingStatusText && (
          <label className="typeahead-location-search-status">
            {searchingStatusText}
          </label>
        )}
      </div>
    </div>
  );
}
